import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Banner, CloseButton } from '@zillow/constellation-10';

import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { useComscorePageTracking } from '@zg-rentals/comscore/client';
import { RequestWidget } from '@zg-rentals/rental-manager-env';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { useSiftScience } from '@zg-rentals/sift-science';
import { ERROR_PAGE_TYPES } from '@zg-rentals/zillow-rental-manager/constants';
import { AppLayoutContainer } from '@zg-rentals/zrm-header-navigation';

import cacheStorageUtils from '../../client/workers/utils/cacheStorage';
import { useRentalManagerWebSocketContext } from '../../contexts/RentalManagerWebSocketContext';
import { useIsInMobileWebView, useIsWizardView } from '../../hooks';
import { isListingsNavContext } from '../../utils/navigationCacheUtils';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { trackComscoreEvent, trackEvent } from '../../analytics';
import { useAuth } from '../AuthProvider';
import ErrorPage from '../ErrorPage';
import IdentityVerificationBanner from '../IdentityVerificationBanner';

type LayoutProps = {
  children: React.ReactNode;
};

export default function Layout({ children }: LayoutProps) {
  const router = useRouter();
  const prevPath = useRef<string>('');

  const { user } = useAuth();
  const isInMobileWebView = useIsInMobileWebView();
  const isWizardView = useIsWizardView();
  const { addMessageListener } = useRentalManagerWebSocketContext();

  const [displayOfflineNotification, setDisplayOfflineNotification] = useState(false);
  const [displayBlockedUserModal, setDisplayBlockedUserModal] = useState(false);

  const hostName = typeof window !== 'undefined' ? window.location.host : 'www.zillow.com';

  const isListingsContext = isListingsNavContext(router.pathname);

  const isChromelessView = isInMobileWebView === true || isWizardView === true;

  const userCanAccessRequestWidget =
    (user?.isMultifamilyCustomer || user?.isSingleFamilyEnterpriseCustomer) &&
    !router.pathname.includes(ROUTES.SUBMIT_REQUEST) &&
    !router.pathname.includes(ROUTES.LISTING);

  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });
  useZrmPageViewEvents(trackEvent.user, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);

  const handleOffline = () => setDisplayOfflineNotification(true);
  const handleOnline = () => setDisplayOfflineNotification(false);

  useEffect(() => {
    if (!user?.loggedIn) {
      cacheStorageUtils.clear();
    }
  }, [user?.loggedIn]);

  // If session expired from 30 minutes of inactivity, while impersonating, we need to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this in production
  useEffect(() => {
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction && user?.impersonatorEmail && user?.impersonatorUserToken && !user?.loggedIn) {
      window.location.href = EXTERNAL_ROUTES.SIGN_IN;
    }
  }, [user?.impersonatorEmail, user?.impersonatorUserToken, user?.loggedIn, router]);

  useEffect(() => {
    if (user && !user?.restrictions?.isAllowed) {
      if (
        router.pathname.toLowerCase().includes(ROUTES.SUBMIT_REQUEST.toLowerCase()) ||
        router.pathname.toLowerCase().includes(ROUTES.FEATURE_FLAGS.toLowerCase()) ||
        router.pathname.toLowerCase().includes(ROUTES.SECURITY_INQUIRY.toLowerCase())
      ) {
        setDisplayBlockedUserModal(false);
      } else {
        setDisplayBlockedUserModal(true);
        if (router.pathname !== ROUTES.PROPERTIES) {
          router.replace(ROUTES.PROPERTIES);
        }
      }
    }

    prevPath.current = router.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.restrictions?.isAllowed, router]);

  useEffect(() => {
    cacheStorageUtils.clearStaleUserSessions(user?.userToken);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?.userToken]);

  return (
    <>
      {displayOfflineNotification && (
        <Banner
          tone="warning"
          closeButton={<CloseButton onImpact />}
          onClose={() => setDisplayOfflineNotification(false)}
        >
          You are browsing offline. Check your internet connection.
        </Banner>
      )}
      {user != null && (
        <AppLayoutContainer
          altLogo={!user?.isEnterpriseCustomer}
          brand={'zillow'}
          userModel={user}
          reactRouterLink={Link}
          host={hostName}
          pathname={typeof window !== 'undefined' ? window.location.href : ''}
          isListingsContext={isListingsContext}
          webSocketSource={addMessageListener}
          displayMainMyProperties={true}
          hideNavigation={isChromelessView}
          hideFooter={isChromelessView}
        >
          <div data-testid="zrmw-next-layout-container">
            {!isChromelessView && <IdentityVerificationBanner />}
            <RpErrorBoundary fallback={<ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />}>
              {children}
            </RpErrorBoundary>
          </div>
        </AppLayoutContainer>
      )}
      {userCanAccessRequestWidget && (
        <RequestWidget
          zrmwUser={user}
          routerLocation={{ pathname: router.pathname, search: router.query.toString() }}
        />
      )}
      {displayBlockedUserModal && user != null && user.restrictions != null && <BlockedUserModal user={user} />}
    </>
  );
}
